export function getStateRank(state) {
  switch (state) {
    case 'done':
      return 0;
    case 'baking':
      return 1;
    case 'preparing':
      return 2;
    case 'queued':
      return 3;
    case 'served':
      return 4;
    default:
      return 5;
  }
}

// Canonical topping order mirrors the server: the order toppings appear in
// data/toppings.yml (categories top to bottom); unknown free-text toppings
// come last, preserving their input order.
export function sortToppingsByConfig(toppings, config) {
  if (!Array.isArray(toppings)) {
    return [];
  }
  const rank = new Map();
  const categories = config?.categories || {};
  let index = 0;
  for (const items of Object.values(categories)) {
    if (!Array.isArray(items)) {
      continue;
    }
    for (const item of items) {
      if (typeof item === 'string' && !rank.has(item)) {
        rank.set(item, index);
        index += 1;
      }
    }
  }
  return toppings
    .map((topping, position) => ({ topping, position }))
    .sort((first, second) => {
      const firstRank = rank.get(first.topping) ?? Infinity;
      const secondRank = rank.get(second.topping) ?? Infinity;
      return firstRank - secondRank || first.position - second.position;
    })
    .map((entry) => entry.topping);
}

export function getVisibleClientOrders(orders) {
  return orders
    .filter((order) => order.state !== 'served')
    .slice()
    .sort((first, second) => getStateRank(first.state) - getStateRank(second.state));
}

export function sortOrdersForDisplay(orders) {
  return orders.slice().sort((a, b) => getStateRank(a.state) - getStateRank(b.state));
}

export function getCustomerOrderIds(customer) {
  if (!customer) return [];
  if (Array.isArray(customer.orderIds)) return customer.orderIds;
  if (customer.orderId) return [customer.orderId];
  return [];
}

export function isCustomerOrder(order, currentCustomer) {
  const ids = getCustomerOrderIds(currentCustomer);
  if (ids.length === 0) return false;
  if (ids.includes(order.id)) return true;
  return Array.isArray(order.parts) && order.parts.some((part) => ids.includes(part.id));
}

// Ready-alerts (bell, vibration, notification) follow a role × view ×
// ownership matrix (role from deriveRole, pinConfigured from the auth status):
// - anonymous: never alerts
// - baker role with BAKER_PIN configured: baker-duty alerts for every order on
//   every page
// - baker role without a configured PIN: baker-duty alerts only while the
//   baker page is active; elsewhere own orders only
// - customer role: own orders only, on any page (including the baker page)
export function shouldAlertOrder(order, mode, currentCustomer, role, pinConfigured) {
  if (role === 'anonymous') return false;
  if (role === 'baker' && (pinConfigured === true || mode === 'baker')) return true;
  return isCustomerOrder(order, currentCustomer);
}

export function getCustomerOrders(orders, currentCustomer) {
  return orders.filter((order) => isCustomerOrder(order, currentCustomer));
}

export function canDeleteOrder(order) {
  return order.state === 'queued' && !order.parts;
}

export function isQueuedHalf(order) {
  return order.state === 'queued' && order.half && !order.parts;
}

export function getFlatToppings(order, config) {
  return [
    ...sortToppingsByConfig(order?.toppings, config),
    ...sortToppingsByConfig(order?.customToppings, config),
  ].filter(Boolean);
}

function getFlatToppingSet(order) {
  return new Set(
    [...(order.toppings || []), ...(order.customToppings || [])].filter(
      (topping) => topping && topping.trim() !== '',
    ),
  );
}

export function getHalfMatchSummary(orderA, orderB) {
  const setA = getFlatToppingSet(orderA);
  const setB = getFlatToppingSet(orderB);
  const all = new Set([...setA, ...setB]);
  let common = 0;
  let additional = 0;
  for (const topping of all) {
    if (setA.has(topping) && setB.has(topping)) {
      common += 1;
    } else {
      additional += 1;
    }
  }
  return { common, additional };
}

export function moveOrderInList(orders, orderId, direction, visibleOrderIds = []) {
  if (!Array.isArray(orders) || orders.length === 0) {
    return orders;
  }

  const visibleIdSet = new Set(visibleOrderIds);
  const visibleOrders = orders.filter((order) => visibleIdSet.has(order.id));
  const currentIndex = visibleOrders.findIndex((order) => order.id === orderId);

  if (currentIndex === -1) {
    return orders;
  }

  const targetIndex = currentIndex + direction;
  if (targetIndex < 0 || targetIndex >= visibleOrders.length) {
    return orders;
  }

  const reorderedVisibleOrderIds = [...visibleOrderIds];
  const [movedOrderId] = reorderedVisibleOrderIds.splice(currentIndex, 1);
  reorderedVisibleOrderIds.splice(targetIndex, 0, movedOrderId);

  return orders.reduce((nextOrders, order) => {
    if (!visibleIdSet.has(order.id)) {
      nextOrders.push(order);
      return nextOrders;
    }

    const nextOrderId = reorderedVisibleOrderIds.shift();
    const nextOrder = orders.find((candidate) => candidate.id === nextOrderId);
    nextOrders.push(nextOrder || order);
    return nextOrders;
  }, []);
}
